import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Search, Edit, Building2, Mail, Phone, MapPin, User, Upload, Eye, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';

const PrimeEmployerManagement = () => {
  const [primeEmployers, setPrimeEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [employerJobs, setEmployerJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [districtSearch, setDistrictSearch] = useState('');
  const districtInputRef = useRef(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const ITEMS_PER_PAGE = 100;

  const [formData, setFormData] = useState({
    employer_name: '',
    logo: '',
    contact_person: '',
    contact_email: '',
    mobile_number: '',
    state: '',
    district: ''
  });

  useEffect(() => {
    fetchPrimeEmployers();
  }, [searchQuery, currentPage]);

  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      setShowDistrictSuggestions(false);
      return;
    }
    try {
      const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(query)}&limit=15`);
      setDistrictSuggestions(response.data);
      setShowDistrictSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching districts:', error);
    }
  };

  const fetchPrimeEmployers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', currentPage);
      params.append('limit', ITEMS_PER_PAGE);
      const response = await api.get(`/admin/prime-employers?${params}`);
      setPrimeEmployers(response.data.prime_employers || response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / ITEMS_PER_PAGE));
    } catch (error) {
      toast.error('Failed to fetch prime employers');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployerJobs = async (employerId) => {
    try {
      setLoadingJobs(true);
      const response = await api.get(`/admin/prime-employer-jobs/${employerId}`);
      setEmployerJobs(response.data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setEmployerJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/upload/logo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, logo: response.data.path });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleSubmit = async () => {
    if (!formData.employer_name.trim()) {
      toast.error('Employer name is required');
      return;
    }

    try {
      if (editingEmployer) {
        await api.put(`/admin/prime-employers/${editingEmployer.id}`, formData);
        toast.success('Prime Employer updated successfully');
      } else {
        await api.post('/admin/prime-employers', formData);
        toast.success('Prime Employer created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchPrimeEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (employer) => {
    setEditingEmployer(employer);
    setFormData({
      employer_name: employer.employer_name || '',
      logo: employer.logo || '',
      contact_person: employer.contact_person || '',
      contact_email: employer.contact_email || '',
      mobile_number: employer.mobile_number || '',
      state: employer.state || '',
      district: employer.district || ''
    });
    setDistrictSearch(employer.state && employer.district ? `${employer.state} / ${employer.district}` : '');
    setShowDialog(true);
  };

  const handleView = async (employer) => {
    setSelectedEmployer(employer);
    setShowViewDialog(true);
    await fetchEmployerJobs(employer.id);
  };

  const handleToggleStatus = async (employer) => {
    try {
      await api.put(`/admin/prime-employers/${employer.id}/toggle-status`);
      toast.success(`Prime Employer ${employer.active ? 'deactivated' : 'activated'}`);
      fetchPrimeEmployers();
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const handleToggleJobStatus = async (jobId, currentActive) => {
    try {
      await api.put(`/admin/jobs/${jobId}/toggle-active`);
      toast.success(`Job ${currentActive ? 'deactivated' : 'activated'}`);
      if (selectedEmployer) {
        await fetchEmployerJobs(selectedEmployer.id);
      }
    } catch (error) {
      toast.error('Failed to toggle job status');
    }
  };

  const resetForm = () => {
    setEditingEmployer(null);
    setFormData({
      employer_name: '',
      logo: '',
      contact_person: '',
      contact_email: '',
      mobile_number: '',
      state: '',
      district: ''
    });
    setDistrictSearch('');
  };

  const backendUrl = getBackendUrl();

  const getLogoUrl = (logo) => {
    return getAssetUrl(logo);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Prime Employers</h1>
          <p className="text-slate-600 text-sm mt-1">Manage employers for admin/executive job posting ({total} total)</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="add-prime-employer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Prime Employer
        </Button>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              data-testid="search-prime-employer"
              placeholder="Search by name, contact person, or email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Prime Employers Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead>Serial No</TableHead>
                <TableHead className="w-16">Logo</TableHead>
                <TableHead>Employer Name</TableHead>
                <TableHead>Contact Person</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-center">Jobs</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : primeEmployers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">No prime employers found</p>
                  </TableCell>
                </TableRow>
              ) : (
                primeEmployers.map((employer, index) => (
                  <TableRow key={employer.id} className={`hover:bg-slate-50 ${!employer.active ? 'opacity-60 bg-slate-50' : ''}`}>
                    <TableCell className="text-center font-medium text-slate-500">
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </TableCell>
                    <TableCell>
                      {employer.serial_number ? (
                        <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-mono font-medium">
                          {employer.serial_number}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="w-10 h-10 rounded border border-slate-200 overflow-hidden flex items-center justify-center bg-white">
                        {employer.logo ? (
                          <img 
                            src={getLogoUrl(employer.logo)} 
                            alt={employer.employer_name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <Building2 className={`w-5 h-5 text-slate-300 ${employer.logo ? 'hidden' : ''}`} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-slate-900" title={employer.employer_name}>
                        {employer.employer_name?.length > 25 ? employer.employer_name.substring(0, 25) + '...' : employer.employer_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {employer.contact_person || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {employer.mobile_number || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      <span title={employer.contact_email}>
                        {employer.contact_email?.length > 20 ? employer.contact_email.substring(0, 20) + '...' : employer.contact_email || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {employer.district && employer.state ? (
                        <span title={`${employer.district}, ${employer.state}`}>
                          {employer.district?.length > 15 ? employer.district.substring(0, 15) + '...' : employer.district}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {employer.job_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={employer.active}
                          onCheckedChange={() => handleToggleStatus(employer)}
                          data-testid={`toggle-status-${employer.id}`}
                        />
                        <span className={`text-xs ${employer.active ? 'text-green-600' : 'text-red-600'}`}>
                          {employer.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(employer)}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Details & Jobs"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(employer)}
                          className="text-slate-600 hover:text-blue-600"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total} employers
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* View Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              Prime Employer Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployer && (
            <div className="space-y-6">
              {/* Employer Info */}
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-20 h-20 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center bg-white flex-shrink-0">
                  {selectedEmployer.logo ? (
                    <img 
                      src={getLogoUrl(selectedEmployer.logo)} 
                      alt={selectedEmployer.employer_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900">{selectedEmployer.employer_name}</h3>
                  <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm">
                    {selectedEmployer.contact_person && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <User className="w-4 h-4" />
                        {selectedEmployer.contact_person}
                      </div>
                    )}
                    {selectedEmployer.mobile_number && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4" />
                        {selectedEmployer.mobile_number}
                      </div>
                    )}
                    {selectedEmployer.contact_email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        {selectedEmployer.contact_email}
                      </div>
                    )}
                    {selectedEmployer.state && selectedEmployer.district && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4" />
                        {selectedEmployer.district}, {selectedEmployer.state}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${selectedEmployer.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {selectedEmployer.active ? 'Active' : 'Inactive'}
                </div>
              </div>

              {/* Jobs Section */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-teal-600" />
                  Jobs Posted ({employerJobs.length})
                </h4>
                
                {loadingJobs ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                  </div>
                ) : employerJobs.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <Briefcase className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No jobs posted yet</p>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>#</TableHead>
                          <TableHead>Serial No</TableHead>
                          <TableHead>Job Title</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Posted On</TableHead>
                          <TableHead>Last Date</TableHead>
                          <TableHead className="text-center">Vacancies</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Active</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employerJobs.map((job, index) => (
                          <TableRow key={job.id} className={!job.active ? 'opacity-60 bg-slate-50' : ''}>
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell>
                              {job.serial_number ? (
                                <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-mono font-medium">
                                  {job.serial_number}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-slate-900">{job.job_title}</div>
                              <div className="text-xs text-slate-500">{job.job_type}</div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {job.locations?.[0]?.district || job.locations?.[0]?.city || 'N/A'}
                              {job.locations?.length > 1 && ` +${job.locations.length - 1}`}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{formatDate(job.created_at)}</TableCell>
                            <TableCell className="text-sm text-slate-600">{formatDate(job.last_date)}</TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                {job.vacancies}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                job.status === 'approved' ? 'bg-green-100 text-green-700' :
                                job.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {job.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Switch
                                  checked={job.active}
                                  onCheckedChange={() => handleToggleJobStatus(job.id, job.active)}
                                  data-testid={`toggle-job-${job.id}`}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEmployer ? 'Edit Prime Employer' : 'Add Prime Employer'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Company Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50">
                  {formData.logo ? (
                    <img 
                      src={getLogoUrl(formData.logo)} 
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload Logo</span>
                  </div>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
            </div>

            {/* Employer Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Employer Name <span className="text-red-500">*</span></label>
              <Input
                data-testid="employer-name-input"
                value={formData.employer_name}
                onChange={(e) => setFormData({ ...formData, employer_name: e.target.value })}
                placeholder="Enter employer/company name"
              />
            </div>

            {/* Contact Person */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Contact Person</label>
              <Input
                data-testid="contact-person-input"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Name of contact person"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mobile Number</label>
              <Input
                data-testid="mobile-input"
                value={formData.mobile_number}
                onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="10-digit mobile number"
                maxLength={10}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <Input
                data-testid="email-input"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="Contact email address"
              />
            </div>

            {/* Location - District Search */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Location (District)</label>
              <div className="relative" ref={districtInputRef}>
                <Input
                  data-testid="district-search-input"
                  value={districtSearch}
                  onChange={(e) => {
                    setDistrictSearch(e.target.value);
                    searchDistricts(e.target.value);
                  }}
                  onFocus={() => districtSearch.length >= 2 && setShowDistrictSuggestions(true)}
                  placeholder="Type district name to search..."
                />
                {showDistrictSuggestions && districtSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {districtSuggestions.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                        onClick={() => {
                          setFormData({ ...formData, state: item.state, district: item.district });
                          setDistrictSearch(`${item.state} / ${item.district}`);
                          setShowDistrictSuggestions(false);
                        }}
                      >
                        <span className="text-slate-900">{item.district}</span>
                        <span className="text-slate-500 ml-2">({item.state})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700">
              {editingEmployer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrimeEmployerManagement;
